import { GlUboSystem, GpuUboSystem } from "pixi.js";

/**
 * Parche para `pixi.js/unsafe-eval`. En 8.19.0 su polyfill del sync de UBOs
 * devuelve una función `(uniforms, data, offset)`, pero `UboSystem
 * .syncUniformGroup` la llama como `(uniforms, data, dataInt32, offset)` (la
 * firma de la versión generada con `new Function`). Consecuencias:
 * - `offset` se ignora (recibe `dataInt32`): cada grupo sincronizado en el
 *   batch buffer se escribe en el offset 0, el de `globalUniforms`. Con un solo
 *   grupo por frame coincide por casualidad; en cuanto un Mesh/Graphics NO
 *   batcheado agrega su `localUniforms`, pisa la matriz de proyección global y
 *   el frame entero sale corrupto (WebGPU). Los grupos con buffer propio
 *   (uniforms de un shader custom) directamente no se escriben.
 * - los uniforms `i32` se escriben como float (bits erróneos).
 * Pixi 8.20.1 corrige la firma, pero no la escritura de enteros. Conservar esa
 * parte del parche; los grupos sin enteros usan el sync nuevo directamente.
 *
 * Importar INMEDIATAMENTE después de `pixi.js/unsafe-eval` (main.tsx).
 */

type UboSync = (
  uniforms: Record<string, unknown>,
  data: Float32Array,
  dataInt32: Int32Array | null,
  offset: number,
) => void;

type LooseSync = (...args: unknown[]) => void;

/** Elemento del layout del UBO (forma mínima de `UboElement`, que Pixi no exporta). */
export interface UboElementLike {
  data: { name: string; type: string; size?: number };
  /** Offset en BYTES dentro del buffer. */
  offset: number;
}

interface UboSystemProto {
  _generateUboSync?: (elements: UboElementLike[]) => LooseSync;
}

const INT_COMPONENTS: Record<string, number> = {
  i32: 1,
  "vec2<i32>": 2,
  "vec3<i32>": 3,
  "vec4<i32>": 4,
};

/**
 * Adapta un sync con firma de polyfill `(uniforms, data, offset)` a la firma
 * real `(uniforms, data, dataInt32, offset)`, o conserva la firma de 8.20.1.
 * Los enteros los re-escribe en la vista i32 cuando la hay (en el batch
 * buffer Pixi tampoco los soporta).
 */
export const wrapPolyfillUboSync = (
  elements: UboElementLike[],
  polyfill: LooseSync,
): UboSync => {
  const ints = elements
    .filter(
      (e) =>
        INT_COMPONENTS[e.data.type] !== undefined && (e.data.size ?? 1) === 1,
    )
    .map((e) => ({
      name: e.data.name,
      offset: e.offset / 4,
      n: INT_COMPONENTS[e.data.type],
    }));
  const hasCurrentSignature = polyfill.length >= 4;
  if (hasCurrentSignature && ints.length === 0) return polyfill as UboSync;
  return (uniforms, data, dataInt32, offset) => {
    if (hasCurrentSignature) polyfill(uniforms, data, dataInt32, offset);
    else polyfill(uniforms, data, offset);
    if (!dataInt32) return;
    for (const it of ints) {
      const v = uniforms[it.name];
      if (it.n === 1) {
        dataInt32[offset + it.offset] = v as number;
      } else {
        const arr = v as ArrayLike<number>;
        for (let i = 0; i < it.n; i += 1) {
          dataInt32[offset + it.offset + i] = arr[i];
        }
      }
    }
  };
};

/**
 * Envuelve `_generateUboSync` del prototipo dado. Devuelve false si no hay
 * nada que parchear (sin polyfill instalado).
 */
export const installPixiUnsafeEvalUboFix = (proto: object): boolean => {
  const p = proto as UboSystemProto;
  const original = p._generateUboSync;
  if (typeof original !== "function") return false;
  p._generateUboSync = function (this: unknown, elements) {
    const sync = original.call(this, elements);
    return wrapPolyfillUboSync(elements, sync) as LooseSync;
  };
  return true;
};

installPixiUnsafeEvalUboFix(GpuUboSystem.prototype);
installPixiUnsafeEvalUboFix(GlUboSystem.prototype);
